import { PRACTICE_BANDS, type PracticeBand } from "./practiceBands";
import {
  generatorMatchesGrade,
  PRACTICE_GENERATOR_CATALOG,
  practiceGeneratorsFor,
} from "./practiceGeneratorCatalog";
import { gradeSpanIncludes } from "./practiceGrades";
import { PRACTICE_QUESTION_TEMPLATES } from "./practiceQuestionTemplates";
import type {
  PracticeDifficulty,
  PracticeQuestionTemplate,
} from "./practiceQuestionTemplates";
import { PRACTICE_SUBJECTS, type PracticeSubject } from "./practiceSubjects";

export function practiceTemplatesForBand(
  band: PracticeBand,
  subject?: string,
): PracticeQuestionTemplate[] {
  return PRACTICE_QUESTION_TEMPLATES.filter((template) => {
    if (template.band !== band) return false;
    if (subject && template.subject !== subject) return false;
    return true;
  });
}

export function practiceSubjectsForBand(band: PracticeBand): PracticeSubject[] {
  const present = new Set(
    practiceTemplatesForBand(band).map((template) => template.subject),
  );
  return PRACTICE_SUBJECTS.filter((subject) => present.has(subject));
}

export function practiceTagsForBandSubject(
  band: PracticeBand,
  subject: string,
): string[] {
  const tags = new Set<string>();
  for (const template of practiceTemplatesForBand(band, subject)) {
    for (const tag of template.tags ?? []) {
      if (tag) tags.add(tag);
    }
  }
  return [...tags];
}

export function practiceBandsWithCatalog(): PracticeBand[] {
  return PRACTICE_BANDS.filter(
    (band) => practiceTemplatesForBand(band).length > 0,
  );
}

export function templateMatchesGrade(
  template: PracticeQuestionTemplate,
  grade: number | null | undefined,
): boolean {
  if (grade == null) return true;
  return gradeSpanIncludes(grade, template.gradeMin, template.gradeMax);
}

/** Static templates plus parameterized generators whose span contains the grade. */
export function practiceGradeOfferingCount(grade: number): number {
  const templates = PRACTICE_QUESTION_TEMPLATES.filter((template) =>
    templateMatchesGrade(template, grade),
  ).length;
  const generated = PRACTICE_GENERATOR_CATALOG.filter((row) =>
    generatorMatchesGrade(row, grade),
  ).length;
  return templates + generated;
}

/** Skill ids for a band and subject, in catalog order, each once. */
export function practiceSkillIdsForBandSubject(
  band: PracticeBand,
  subject: string,
): string[] {
  return practiceSkillIdsForOffering(band, subject, null);
}

/**
 * Skills a student can actually practice at this grade: in-grade templates
 * and the generators offered for that grade. A grade 3 list must not include
 * a grade 5 template such as order of operations.
 */
export function practiceSkillIdsForOffering(
  band: PracticeBand,
  subject: string,
  grade: number | null,
): string[] {
  const ids: string[] = [];
  const add = (skillId: string | undefined) => {
    if (skillId && !ids.includes(skillId)) ids.push(skillId);
  };
  for (const template of practiceTemplatesForBand(band, subject)) {
    if (!templateMatchesGrade(template, grade)) continue;
    add(template.skillId);
  }
  for (const row of practiceGeneratorsFor(band, subject, grade)) {
    add(row.skillId);
  }
  return ids;
}

/**
 * The next exercise inside one grade. A matching generator wins, so grade 3
 * stays on grade 3 number skills instead of a later static template.
 */
export function pickGradeOffering(opts: {
  band: PracticeBand;
  subject: string;
  grade: number | null;
  skillId?: string | null;
  difficulty?: PracticeDifficulty | null;
  excludeGeneratorId?: string;
  excludeTemplateId?: string;
}): { generatorId: string; grade: number } | { templateUuid: string } | null {
  const generators = practiceGeneratorsFor(opts.band, opts.subject, opts.grade).filter(
    (row) => row.id !== opts.excludeGeneratorId,
  );
  const skilled = opts.skillId
    ? generators.filter((row) => row.skillId === opts.skillId)
    : generators;
  const pool = skilled.length ? skilled : opts.skillId ? [] : generators;
  const generator =
    pool.find((row) => row.difficulty === opts.difficulty) ??
    pool.find((row) => row.difficulty === "intro") ??
    pool[0];
  if (generator) {
    const grade =
      opts.grade != null &&
      opts.grade >= generator.gradeMin &&
      opts.grade <= generator.gradeMax
        ? opts.grade
        : generator.gradeMin;
    return { generatorId: generator.id, grade };
  }

  const templates = PRACTICE_QUESTION_TEMPLATES.filter((template) => {
    if (template.band !== opts.band || template.subject !== opts.subject) return false;
    if (template.uuid === opts.excludeTemplateId) return false;
    if (!templateMatchesGrade(template, opts.grade)) return false;
    if (
      opts.skillId &&
      template.skillId !== opts.skillId &&
      !template.tags?.includes(opts.skillId)
    ) {
      return false;
    }
    return true;
  });
  const template =
    templates.find((row) => row.difficulty === opts.difficulty) ?? templates[0];
  if (!template) return null;
  return { templateUuid: template.uuid };
}
